import type { SubmissionStatus } from '../types'

export function formatDate(value: string | null | undefined): string {
  if (!value) return '-'
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '-'
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(1)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

export function statusLabel(status: SubmissionStatus): string {
  const labels: Record<SubmissionStatus, string> = {
    queued: 'In queue',
    in_progress: 'Being read',
    completed: 'Feedback available',
    withdrawn: 'Withdrawn',
    unreadable: 'Could not be opened',
    declined_conflict: 'Not read: research overlap',
    rejected_security: 'Not read: security issue',
  }
  return labels[status]
}

export function isFinalStatus(status: SubmissionStatus): boolean {
  return !['queued', 'in_progress'].includes(status)
}

export async function hasPdfSignature(file: File): Promise<boolean> {
  const firstBytes = new Uint8Array(await file.slice(0, 5).arrayBuffer())
  return new TextDecoder().decode(firstBytes) === '%PDF-'
}

export function safeFileBaseName(name: string): string {
  return name.replace(/\.pdf$/i, '').replace(/[^a-zA-Z0-9_-]+/g, '-').slice(0, 60)
}

export function normalizeArxivIdentifier(value: string): string | null {
  let candidate = value.trim()
  if (!candidate) return null

  candidate = candidate.replace(
    /^https?:\/\/(?:www\.)?arxiv\.org\/(?:abs|pdf)\//i,
    '',
  )
  candidate = candidate.replace(/^arxiv:\s*/i, '')
  candidate = candidate.split(/[?#]/, 1)[0]
  candidate = candidate.replace(/\.pdf$/i, '')
  candidate = candidate.replace(/v\d+$/i, '')
  candidate = candidate.trim()

  const currentStyle = /^\d{4}\.\d{4,5}$/
  const legacyStyle = /^[a-z0-9.-]+\/\d{7}$/i

  if (!currentStyle.test(candidate) && !legacyStyle.test(candidate)) {
    return null
  }

  return candidate.toLowerCase()
}

export function formatArxivIdentifier(identifier: string): string {
  return `arXiv:${identifier}`
}

function encodeArxivPath(identifier: string): string {
  return identifier.split('/').map(encodeURIComponent).join('/')
}

export function getArxivAbstractUrl(identifier: string): string {
  return `https://arxiv.org/abs/${encodeArxivPath(identifier)}`
}

export function getArxivPdfUrl(identifier: string): string {
  return `https://arxiv.org/pdf/${encodeArxivPath(identifier)}`
}
